import { ref, shallowRef, onMounted, onUnmounted, nextTick } from 'vue'
import * as echarts from 'echarts'

export function useECharts() {
  const chartRef = ref<HTMLElement>()
  const chartInstance = shallowRef<echarts.ECharts>()
  const isLoading = ref(false)
  let resizeObserver: ResizeObserver | null = null
  let resizeTimeout: NodeJS.Timeout | null = null

  const initChart = async (options: echarts.EChartsCoreOption) => {
    if (!chartRef.value) return

    await nextTick()

    try {
      isLoading.value = true

      // Dispose existing chart if it exists
      if (chartInstance.value) {
        chartInstance.value.dispose()
        chartInstance.value = undefined
      }

      // Clean up existing resize observer
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }

      // Initialize chart with proper configuration
      chartInstance.value = echarts.init(chartRef.value, null, {
        renderer: 'canvas',
        useDirtyRect: false
      })

      // Suppress ECharts console warnings
      const originalConsoleWarn = console.warn
      console.warn = function(...args) {
        // Suppress specific ECharts warnings
        if (args[0] && typeof args[0] === 'string') {
          const message = args[0]
          if (message.includes('alignTicks') || 
              message.includes('The ticks may be not readable') ||
              message.includes('[ECharts]')) {
            return // Suppress these warnings
          }
        }
        // Allow other warnings to pass through
        originalConsoleWarn.apply(console, args)
      }

      // Set options with proper animation settings
      chartInstance.value.setOption(options, true, true)

      // Set up ResizeObserver after animation completes
      if (options.animation && options.animationDuration) {
        // Wait for animation to complete before setting up ResizeObserver
        setTimeout(
          () => {
            setupResizeObserver()
          },
          (options.animationDuration as number) + 500
        ) // Animation duration + 500ms buffer
      } else if (options.animation) {
        // Animation is enabled but no custom duration specified - use ECharts default
        setTimeout(() => {
          setupResizeObserver()
        }, 1500) // Default 1000ms + 500ms buffer
      } else {
        // No animation, set up ResizeObserver immediately with minimal delay
        setTimeout(() => {
          setupResizeObserver()
        }, 500) // Just a small delay to ensure chart is fully rendered
      }

      await nextTick()
    } catch (error) {
      console.error('Error initializing chart:', error)
    } finally {
      isLoading.value = false
    }
  }

  const setupResizeObserver = () => {
    if (!chartRef.value || !chartInstance.value) return

    resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize calls to improve performance
      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }

      resizeTimeout = setTimeout(() => {
        for (const entry of entries) {
          if (chartInstance.value && !chartInstance.value.isDisposed()) {
            try {
              // Check if chart is currently animating before resizing
              const isAnimating = chartInstance.value.getOption().animation

              // Only resize if not animating, or wait for animation to complete
              if (!isAnimating) {
                chartInstance.value.resize()
              } else {
                // If animating, wait a bit longer and try again
                setTimeout(() => {
                  if (chartInstance.value && !chartInstance.value.isDisposed()) {
                    chartInstance.value.resize()
                  }
                }, 200)
              }
            } catch (error) {
              // Silently ignore resize errors
            }
          }
        }
      }, 200) // Increased debounce time to 200ms
    })

    resizeObserver.observe(chartRef.value)
  }

  const updateChart = (options: echarts.EChartsCoreOption) => {
    if (chartInstance.value && !chartInstance.value.isDisposed()) {
      chartInstance.value.setOption(options, true, true)
    }
  }

  const resizeChart = () => {
    if (chartInstance.value && !chartInstance.value.isDisposed()) {
      try {
        chartInstance.value.resize()
      } catch (error) {
        console.warn('Chart resize failed:', error)
      }
    }
  }

  const disposeChart = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
      resizeTimeout = null
    }
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    if (chartInstance.value) {
      chartInstance.value.dispose()
      chartInstance.value = undefined
    }
  }

  onUnmounted(() => {
    disposeChart()
  })

  return {
    chartRef,
    chartInstance,
    isLoading,
    initChart,
    updateChart,
    resizeChart,
    disposeChart
  }
}